import React, { useState, useEffect, useRef } from 'react';

import costSavingsImg from '../assets/ai-workflow/cost_savings.webp';
import scalableAdaptiveImg from '../assets/ai-workflow/scalable_adaptive.webp';
import accuracyImg from '../assets/ai-workflow/accuracy.webp';
import predictiveAnalysisImg from '../assets/ai-workflow/predictive_analysis.webp';
import efficiencyImg from '../assets/ai-workflow/efficiency.webp';
import customerSatisfactionImg from '../assets/ai-workflow/customer_satisfaction.webp';

const AI_WORKFLOW_SLIDES = [
  {
    id: 'cost-savings',
    title: 'Cut Costs by up to 40%',
    description:
      'With intelligent automation and streamlined processes, reduce operational overheads significantly. AI replaces manual tasks with scalable, cost-effective solutions.',
    points: [
      'Remove duplicate data entry and repetitive handoffs.',
      'Redirect saved budget into product and customer growth.'
    ],
    accent: '#ff69b4',
    cardBackground: 'linear-gradient(165deg, rgba(139, 69, 119, 0.95), rgba(75, 37, 64, 0.9))',
    image: costSavingsImg,
    imageAlt: 'Dashboard chart highlighting a drop in operating costs.'
  },
  {
    id: 'scalable-workflows',
    title: 'Scalable and Adaptable Workflows',
    description:
      'Implement workflows that grow alongside your business. Our solutions adapt to emerging needs so your teams stay future-ready.',
    points: [
      'Quickly plug new tools and data sources into orchestrations.',
      'Version workflows without downtime or rework.'
    ],
    accent: '#72cce3',
    cardBackground: 'linear-gradient(165deg, rgba(9, 24, 33, 0.95), rgba(12, 48, 58, 0.88))',
    image: scalableAdaptiveImg,
    imageAlt: 'Team working with multiple connected workflow diagrams.'
  },
  {
    id: 'accuracy',
    title: 'Achieve 99% Accuracy',
    description:
      'Minimize errors and inconsistencies through AI-powered precision. From data entry to customer interactions, every process operates flawlessly.',
    points: [
      'AI validation catches anomalies before they reach your teams.',
      'Policy-aware automations keep compliance locked in.'
    ],
    accent: '#ffb703',
    cardBackground: 'linear-gradient(165deg, rgba(31, 23, 9, 0.95), rgba(70, 47, 6, 0.88))',
    image: accuracyImg,
    imageAlt: 'Magnifying glass inspecting accurate analytics results.'
  },
  {
    id: 'predictive-insights',
    title: 'Gain Predictive Insights',
    description:
      'AI does not stop at automation; it learns. Tap into predictive analytics and real-time reporting to uncover trends and opportunities before they surface.',
    points: [
      'Surface leading indicators with live dashboards.',
      'Feed decisions with scenario modeling and trend alerts.'
    ],
    accent: '#ff8c00',
    cardBackground: 'linear-gradient(165deg, rgba(102, 51, 0, 0.95), rgba(68, 34, 0, 0.88))',
    image: predictiveAnalysisImg,
    imageAlt: 'Analytics interface showing predictive insight visualisations.'
  },
  {
    id: 'efficiency',
    title: 'Boost Efficiency by up to 50%',
    description:
      'AI workflows automate time-consuming and repetitive tasks so teams spend time on strategic initiatives that move the needle.',
    points: [
      'Automate follow-ups, reporting, and escalations in minutes.',
      'Keep humans focused on creative and high-impact decisions.'
    ],
    accent: '#4a90e2',
    cardBackground: 'linear-gradient(165deg, rgba(25, 55, 95, 0.95), rgba(17, 40, 70, 0.9))',
    image: efficiencyImg,
    imageAlt: 'Person reviewing dashboards while automated systems run in background.'
  },
  {
    id: 'customer-experience',
    title: 'Elevate Customer Experience',
    description:
      'Integrate AI agents and chatbots to offer instant, personalized support. Reduce response times and boost customer satisfaction by up to 60%.',
    points: [
      'Use contextual AI answers tailored to every customer profile.',
      'Blend human agents and AI for smoother handoffs.'
    ],
    accent: '#32cd32',
    cardBackground: 'linear-gradient(165deg, rgba(25, 102, 25, 0.95), rgba(17, 68, 17, 0.9))',
    image: customerSatisfactionImg,
    imageAlt: 'Customer receiving assistance from an AI powered agent.'
  }
];

const AIWorkflowShowcase = () => {
  const [currentIndex, setCurrentIndex] = useState(1); // Start at 1 (first real slide)
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef(null);

  // Create extended slides array for seamless infinite scroll
  const extendedSlides = [
    AI_WORKFLOW_SLIDES[AI_WORKFLOW_SLIDES.length - 1], // Last slide at beginning
    ...AI_WORKFLOW_SLIDES, // All original slides
    AI_WORKFLOW_SLIDES[0] // First slide at end
  ];

  const totalSlides = AI_WORKFLOW_SLIDES.length;

  // Preload all images on component mount to prevent lag during transitions
  useEffect(() => {
    AI_WORKFLOW_SLIDES.forEach((slide) => {
      const img = new Image();
      img.src = slide.image;
    });
  }, []);

  // Auto-advance timer
  useEffect(() => {
    if (!isTransitioning) {
      timerRef.current = setTimeout(() => {
        handleNext();
      }, 5000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentIndex, isTransitioning]);

  const resetTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const handleNext = () => {
    if (isTransitioning) return;

    resetTimer();
    setIsTransitioning(true);

    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);

    setTimeout(() => {
      if (nextIndex > totalSlides) {
        // Reset to first real slide (index 1) without transition
        setCurrentIndex(1);
      }
      setIsTransitioning(false);
    }, 500);
  };

  const handlePrev = () => {
    if (isTransitioning) return;

    resetTimer();
    setIsTransitioning(true);

    const prevIndex = currentIndex - 1;
    setCurrentIndex(prevIndex);

    setTimeout(() => {
      if (prevIndex < 1) {
        // Reset to last real slide (index totalSlides) without transition
        setCurrentIndex(totalSlides);
      }
      setIsTransitioning(false);
    }, 500);
  };

  return (
    <div className="ai-workflow-showcase">
      <div className="ai-slider-viewport" aria-live="polite">
        <div
          className="ai-slider-track"
          style={{
            transform: `translateX(-${currentIndex * (100 / extendedSlides.length)}%)`,
            transition: isTransitioning ? 'transform 500ms ease-in-out' : 'none'
          }}
        >
          {extendedSlides.map((slide, index) => (
            <div className="ai-slider-slide" key={`${slide.id}-${index}`}>
              <article
                className="ai-showcase-card"
                style={{ background: slide.cardBackground }}
              >
                <h4 className="ai-card-title">{slide.title}</h4>
                <div className="ai-card-body">
                  <p className="ai-card-description">{slide.description}</p>
                  <ul className="ai-card-points">
                    {slide.points.map(point => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </article>
              <div className="ai-showcase-visual">
                <div className="ai-visual-frame">
                  <img src={slide.image} alt={slide.imageAlt} width="600" height="400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="ai-showcase-controls">
        <button
          type="button"
          className="ai-showcase-arrow"
          onClick={handlePrev}
          aria-label="Previous AI workflow insight"
        >
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        <button
          type="button"
          className="ai-showcase-arrow"
          onClick={handleNext}
          aria-label="Next AI workflow insight"
        >
          <i className="fa-solid fa-arrow-right"></i>
        </button>
      </div>
    </div>
  );
};

export default AIWorkflowShowcase;